/* Generated by restful-react */

import React from 'react';
import {
	Get,
	GetProps,
	useGet,
	UseGetProps,
	Mutate,
	MutateProps,
	useMutate,
	UseMutateProps,
} from 'restful-react';

export interface Error {
	/**
	 * HTTP satus code
	 */
	status: string;
	/**
	 * Code indicating the error type
	 */
	error_code: string;
	/**
	 * More detailed error code, may be used differentiate between variations of the main error type
	 */
	error_sub_code: string;
	/**
	 * For future use
	 */
	description?: string;
	/**
	 * For future use
	 */
	more_info?: string;
	/**
	 * For future use
	 */
	doc_link?: string;
	/**
	 * Custom error fields
	 */
	details?: ErrorDetails[];
}

export interface ErrorDetails {
	/**
	 * Custom error field name
	 */
	field?: string;
	/**
	 * Custom error field value
	 */
	value?: string;
}

export interface CreateTalSsoTokenResponse {
	data: {
		/**
		 * TAL SSO token
		 */
		guid: string;
		/**
		 * TAL Redirect URL
		 */
		redirect_url: string;
	};
}

export interface GetInsuranceResponse {
	data: InsuranceData;
}

export interface InsuranceData {
	/**
	 * effective date
	 */
	effective_date: string;
	/**
	 * member age in years
	 */
	member_age?: number;
	/**
	 * member classification ID
	 */
	member_classification_id?: number;
	insurance_details: InsuranceDetails;
	employment_details: MemberEmploymentDetails;
}

export interface InsuranceDetails {
	/**
	 * definition ID
	 */
	definition_id?: string;
	/**
	 * definition name
	 */
	definition_name: string;
	/**
	 * definition description
	 */
	definition_description: string;
	/**
	 * sub plan
	 */
	sub_plan?: string;
	/**
	 * insurance status
	 */
	status?: string;
	/**
	 * status ID
	 */
	status_id?: number;
	/**
	 * age-based default units upgrade status
	 */
	abd_units_upgrade_status: string;
	insurance_covers?: InsuranceCovers[];
	insurance_types?: InsuranceTypes[];
}

export interface InsuranceCovers {
	/**
	 * insurance cover ID
	 */
	id?: string;
	/**
	 * insurance cover description
	 */
	description?: string;
	/**
	 * insurance cover amount
	 */
	amount?: number;
}

export interface InsuranceTypes {
	/**
	 * insurance type ID
	 */
	id?: string;
	/**
	 * insurace type status
	 */
	status?: string;
	description?: string;
	/**
	 * insurance date
	 */
	insurance_date?: string;
	/**
	 * is employer default
	 */
	is_employer_default?: boolean;
	/**
	 * has exclusions
	 */
	has_exclusions?: {};
	/**
	 * business rating factor
	 */
	business_rating_factor?: number;
	insurance_limits?: InsuranceLimits;
	insurance_options?: InsuranceOptions[];
}

export interface InsuranceLimits {
	/**
	 * forward underwriting limit
	 */
	forward_underwriting_limit?: number;
	/**
	 * amnesty expired
	 */
	is_amnesty_expired: boolean;
}

export interface InsuranceOptions {
	/**
	 * insurance option ID
	 */
	id: string;
	/**
	 * insurance option description
	 */
	description: string;
	/**
	 * insurance option status
	 */
	status?: string;
	/**
	 * unitised
	 */
	is_unitised: boolean;
	/**
	 * insurance formula
	 */
	insurance_formula?: string;
	/**
	 * insurance formula value
	 */
	insurance_formula_value?: string;
	/**
	 * insurance design
	 */
	insurance_design?: string;
	insurance_conditions: InsuranceConditions[];
	covers: Covers[];
}

export interface InsuranceConditions {
	/**
	 * condition type
	 */
	condition_type: string;
	/**
	 * condition value
	 */
	value?: string;
	/**
	 * unit of measure
	 */
	unit_of_measure?: string;
}

export interface Covers {
	/**
	 * cover ID
	 */
	id: string;
	/**
	 * cover type
	 */
	type: string;
	/**
	 * cover status
	 */
	status?: string;
	/**
	 * cover description
	 */
	description?: string;
	/**
	 * basic cover amount
	 */
	basic_cover_amount?: number;
	/**
	 * extra cover amount
	 */
	extra_cover_amount?: number;
}

export interface MemberEmploymentDetails {
	/**
	 * insurance plan
	 */
	insurance_plan?: string;
	/**
	 * insurance category
	 */
	insurance_category?: string;
	/**
	 * occupation
	 */
	occupation?: string;
	/**
	 * individual work rating
	 */
	individual_work_rating: string;
	/**
	 * applied work rating
	 */
	applied_work_rating: string;
	/**
	 * category work rating
	 */
	category_work_rating: string;
	employment?: Employment[];
}

export interface Employment {
	/**
	 * plan employer reference number
	 */
	employer_number: string;
	/**
	 * employer name
	 */
	employer_business_name: string;
	/**
	 * employee salary
	 */
	salary?: number;
	/**
	 * employment type
	 */
	employment_type?: string;
}

/**
 * Bad request
 */
export type Response400 = Error;

/**
 * Unauthorized
 */
export type Response401 = Error;

/**
 * Not found
 */
export type Response404 = Error;

/**
 * Internal server error
 */
export type Response500 = Error;

/**
 * An error has occurred
 */
export type ErrorResponseResponse = Error;

export interface GetInsuranceQueryParams {
	/**
	 * Insurace effective dates
	 */
	effective_dates?: string[];
}

export interface GetInsurancePathParams {
	/**
	 * Product type. i.e Accumulation or Pension (case sensitive)
	 */
	product_name: 'Accumulation' | 'Pension';
	/**
	 * AustralianSuper Member Account Number
	 */
	account_number: string;
}

export type GetInsuranceProps = Omit<
	GetProps<
		GetInsuranceResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetInsuranceQueryParams,
		GetInsurancePathParams
	>,
	'path'
> &
	GetInsurancePathParams;

/**
 * Get member insurance data
 */
export const GetInsurance = ({
	product_name,
	account_number,
	...props
}: GetInsuranceProps) => (
	<Get<
		GetInsuranceResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetInsuranceQueryParams,
		GetInsurancePathParams
	>
		path={`/insurances/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseGetInsuranceProps = Omit<
	UseGetProps<
		GetInsuranceResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetInsuranceQueryParams,
		GetInsurancePathParams
	>,
	'path'
> &
	GetInsurancePathParams;

/**
 * Get member insurance data
 */
export const useGetInsurance = ({
	product_name,
	account_number,
	...props
}: UseGetInsuranceProps) =>
	useGet<
		GetInsuranceResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetInsuranceQueryParams,
		GetInsurancePathParams
	>(
		(paramsInPath: GetInsurancePathParams) =>
			`/insurances/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export interface GetTalSsoTokenPathParams {
	/**
	 * Product type. i.e Accumulation or Pension (case sensitive)
	 */
	product_name: 'Accumulation' | 'Pension';
	/**
	 * AustralianSuper Member Account Number
	 */
	account_number: string;
}

export type GetTalSsoTokenProps = Omit<
	MutateProps<
		CreateTalSsoTokenResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void,
		GetTalSsoTokenPathParams
	>,
	'path' | 'verb'
> &
	GetTalSsoTokenPathParams;

/**
 * Create SSO Token, GET TAL redirect URL
 */
export const GetTalSsoToken = ({
	product_name,
	account_number,
	...props
}: GetTalSsoTokenProps) => (
	<Mutate<
		CreateTalSsoTokenResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void,
		GetTalSsoTokenPathParams
	>
		verb="POST"
		path={`/insurances/${product_name}/${account_number}/tal-sso`}
		{...props}
	/>
);

export type UseGetTalSsoTokenProps = Omit<
	UseMutateProps<
		CreateTalSsoTokenResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void,
		GetTalSsoTokenPathParams
	>,
	'path' | 'verb'
> &
	GetTalSsoTokenPathParams;

/**
 * Create SSO Token, GET TAL redirect URL
 */
export const useGetTalSsoToken = ({
	product_name,
	account_number,
	...props
}: UseGetTalSsoTokenProps) =>
	useMutate<
		CreateTalSsoTokenResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void,
		GetTalSsoTokenPathParams
	>(
		'POST',
		(paramsInPath: GetTalSsoTokenPathParams) =>
			`/insurances/${paramsInPath.product_name}/${paramsInPath.account_number}/tal-sso`,
		{ pathParams: { product_name, account_number }, ...props },
	);
